import type {
  BlockEffects,
  BlockTextStyle,
  GradientOverlay,
  BackgroundImage,
  BlockBorderSide,
  BlockBorder,
  BlockCornerRadius,
  BlockLayoutStyle,
  BlockVisibility,
  BlockAttributes,
  ResponsiveFlowPatch,
  ResponsiveStylePatch,
  BlockStyle,
} from "./types";

/**
 * Breakpoints du CMS (alignés Tailwind, modèle desktop-first façon Elementor) :
 * mobile ≤ 767, tablette 768–1023, bureau ≥ 1024. Une valeur non surchargée
 * HÉRITE du breakpoint supérieur (desktop → tablette → mobile).
 */
export const CMS_STYLE_BREAKPOINTS = { tabletMax: 1023, mobileMax: 767 } as const;

export const DEFAULT_BLOCK_EFFECTS: BlockEffects = {
  sticky: false,
  stickyTop: 0,
  entrance: "none",
};

export const DEFAULT_BLOCK_TEXT_STYLE: BlockTextStyle = {
  fontSize: null,
  align: null,
  transform: null,
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  lineHeight: null,
  letterSpacing: null,
};

export const DEFAULT_GRADIENT_OVERLAY: GradientOverlay = {
  enabled: false,
  from: null,
  to: null,
  direction: "to-bottom",
  opacity: 0.4,
};

export const DEFAULT_BACKGROUND_IMAGE: BackgroundImage = {
  attachmentId: null,
  fit: "cover",
  position: "center",
  overlay: { ...DEFAULT_GRADIENT_OVERLAY },
};

const DEFAULT_BLOCK_BORDER_SIDE: BlockBorderSide = {
  width: null,
  style: "solid",
  color: null,
};

export const DEFAULT_BLOCK_BORDER: BlockBorder = {
  top: { ...DEFAULT_BLOCK_BORDER_SIDE },
  right: { ...DEFAULT_BLOCK_BORDER_SIDE },
  bottom: { ...DEFAULT_BLOCK_BORDER_SIDE },
  left: { ...DEFAULT_BLOCK_BORDER_SIDE },
};

export const DEFAULT_BLOCK_RADIUS: BlockCornerRadius = {
  tl: null,
  tr: null,
  br: null,
  bl: null,
};

export const DEFAULT_BLOCK_LAYOUT: BlockLayoutStyle = {
  width: null,
  widthUnit: "%",
  maxWidth: null,
  align: null,
  contentMaxWidth: null,
  zIndex: null,
};

export const DEFAULT_BLOCK_VISIBILITY: BlockVisibility = {
  hideDesktop: false,
  hideTablet: false,
  hideMobile: false,
};

export const DEFAULT_BLOCK_ATTRIBUTES: BlockAttributes = {
  cssId: null,
  cssClass: null,
};

/** Figé : partagé par `DEFAULT_RESPONSIVE_PATCH`, toujours copié avant usage. */
export const DEFAULT_RESPONSIVE_FLOW: Readonly<ResponsiveFlowPatch> = Object.freeze({
  direction: null,
  alignItems: null,
  justify: null,
  gap: null,
  columnGap: null,
  rowGap: null,
  columns: null,
});

export const DEFAULT_RESPONSIVE_PATCH: ResponsiveStylePatch = {
  margin: { top: null, right: null, bottom: null, left: null },
  padding: { top: null, right: null, bottom: null, left: null },
  width: null,
  fontSize: null,
  letterSpacing: null,
  lineHeight: null,
  textAlign: null,
  flow: { ...DEFAULT_RESPONSIVE_FLOW },
};

export function cloneResponsivePatch(): ResponsiveStylePatch {
  return {
    margin: { ...DEFAULT_RESPONSIVE_PATCH.margin },
    padding: { ...DEFAULT_RESPONSIVE_PATCH.padding },
    width: null,
    fontSize: null,
    letterSpacing: null,
    lineHeight: null,
    textAlign: null,
    flow: { ...DEFAULT_RESPONSIVE_FLOW },
  };
}

/**
 * Classe posée sur l'élément flex / grid des blocs conteneurs (Conteneur,
 * Colonnes, Grille), de Texte + image et des grilles d'œuvres du catalogue :
 * les surcharges `responsive.*.flow` du bloc la ciblent.
 */
export const CMS_FLOW_CLASS = "cms-flow";

export const DEFAULT_BLOCK_STYLE: BlockStyle = {
  design: {
    background: null,
    backgroundImage: {
      ...DEFAULT_BACKGROUND_IMAGE,
      overlay: { ...DEFAULT_GRADIENT_OVERLAY },
    },
    textColor: null,
    typography: { fontFamily: null, fontVariant: null },
    text: { ...DEFAULT_BLOCK_TEXT_STYLE },
    border: {
      top: { ...DEFAULT_BLOCK_BORDER_SIDE },
      right: { ...DEFAULT_BLOCK_BORDER_SIDE },
      bottom: { ...DEFAULT_BLOCK_BORDER_SIDE },
      left: { ...DEFAULT_BLOCK_BORDER_SIDE },
    },
    radius: { ...DEFAULT_BLOCK_RADIUS },
    shadow: "none",
  },
  spacing: {
    // 0 = défaut éditable dans « Marge extérieure / intérieure ».
    // Pas d'espacement en dur dans les composants : tout passe par Design.
    margin: { top: 0, right: 0, bottom: 0, left: 0 },
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
    unit: "px",
  },
  layout: { ...DEFAULT_BLOCK_LAYOUT },
  effects: { ...DEFAULT_BLOCK_EFFECTS },
  visibility: { ...DEFAULT_BLOCK_VISIBILITY },
  attributes: { ...DEFAULT_BLOCK_ATTRIBUTES },
  responsive: {
    tablet: cloneResponsivePatch(),
    mobile: cloneResponsivePatch(),
  },
};
